import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageSquareQuote,
  Star,
  Search,
  Plus,
  CheckCircle,
  XCircle,
  Clock,
  Trash2,
  Edit3,
  Loader2,
  X,
  AlertCircle,
  StarOff,
} from 'lucide-react';
import clsx from 'clsx';
import {
  useAdminReviews,
  useAdminReviewStats,
  useUpdateReviewStatus,
  useToggleReviewFeatured,
  useUpdateReview,
  useDeleteReview,
  useCreateReview,
  MAX_REVIEW_LENGTH,
} from '../../services/reviewService';
import { useAdminProducts } from '../../services/adminService';
import { StarRating } from '../../components/StarRating/StarRating';

const STATUS_TABS = [
  { key: 'all', label: 'All', icon: MessageSquareQuote },
  { key: 'pending', label: 'Pending', icon: Clock, color: 'text-warning' },
  { key: 'approved', label: 'Approved', icon: CheckCircle, color: 'text-success' },
  { key: 'rejected', label: 'Rejected', icon: XCircle, color: 'text-error' },
];

const overlayVariants = { hidden: { opacity: 0 }, visible: { opacity: 1 }, exit: { opacity: 0 } };
const panelVariants = {
  hidden: { x: '100%' },
  visible: { x: 0, transition: { type: 'spring', damping: 30, stiffness: 300 } },
  exit: { x: '100%', transition: { duration: 0.2 } },
};

export default function AdminTestimonialsPage() {
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [editReview, setEditReview] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const { data: reviews, isLoading } = useAdminReviews(activeTab);
  const { data: stats } = useAdminReviewStats();
  const statusMutation = useUpdateReviewStatus();
  const featuredMutation = useToggleReviewFeatured();
  const updateMutation = useUpdateReview();
  const deleteMutation = useDeleteReview();

  const filtered = (reviews || []).filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      r.name?.toLowerCase().includes(q) ||
      r.text?.toLowerCase().includes(q) ||
      r.products?.title?.toLowerCase().includes(q)
    );
  });

  const handleApprove = (id) => statusMutation.mutate({ id, status: 'approved' });
  const handleReject = (id) => statusMutation.mutate({ id, status: 'rejected' });
  const handleDelete = async (id) => {
    await deleteMutation.mutateAsync(id);
    setDeleteConfirm(null);
  };

  const statCards = [
    { label: 'Total', value: stats?.total || 0, icon: MessageSquareQuote, bg: 'bg-blue-50', color: 'text-blue-600' },
    { label: 'Pending', value: stats?.pending || 0, icon: Clock, bg: 'bg-amber-50', color: 'text-amber-600' },
    { label: 'Approved', value: stats?.approved || 0, icon: CheckCircle, bg: 'bg-green-50', color: 'text-green-600' },
    { label: 'Rejected', value: stats?.rejected || 0, icon: XCircle, bg: 'bg-red-50', color: 'text-red-600' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-brand-dark">Reviews & Testimonials</h1>
          <p className="text-sm text-brand-gray mt-1">Manage customer reviews and testimonials</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold bg-brand-red hover:bg-red-700 text-brand-white rounded-xl transition-colors"
        >
          <Plus size={14} /> Add Review
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {statCards.map((card, i) => {
          const Icon = card.icon;
          return (
            <motion.div
              key={card.label}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: i * 0.05 }}
              className="bg-brand-white border border-ui-border rounded-2xl p-4"
            >
              <div className="flex items-center gap-3">
                <div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center', card.bg)}>
                  <Icon size={18} className={card.color} />
                </div>
                <div>
                  <p className="text-xs font-medium text-brand-gray">{card.label}</p>
                  <p className="text-xl font-extrabold text-brand-dark">{card.value}</p>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Tabs + Search */}
      <div className="bg-brand-white border border-ui-border rounded-2xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-6 py-4 border-b border-ui-border">
          <div className="flex items-center gap-1 overflow-x-auto">
            {STATUS_TABS.map((tab) => {
              const Icon = tab.icon;
              const count = tab.key === 'all' ? stats?.total : stats?.[tab.key];
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={clsx(
                    'flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg transition-colors whitespace-nowrap',
                    activeTab === tab.key
                      ? 'bg-brand-red text-brand-white'
                      : 'text-brand-gray hover:bg-brand-light'
                  )}
                >
                  <Icon size={12} className={activeTab === tab.key ? '' : tab.color || ''} />
                  {tab.label}
                  {count !== undefined && (
                    <span className={clsx(
                      'text-[10px] px-1.5 py-0.5 rounded-full font-bold',
                      activeTab === tab.key ? 'bg-brand-white/20' : 'bg-brand-light'
                    )}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="relative w-full sm:w-60">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ui-muted" />
            <input
              type="text"
              placeholder="Search reviews..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-8 pr-4 py-2 text-sm bg-brand-light border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted"
            />
          </div>
        </div>

        {/* Review list */}
        <div className="divide-y divide-ui-border">
          {isLoading ? (
            <div className="p-8 flex items-center justify-center">
              <Loader2 size={24} className="animate-spin text-brand-red" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <MessageSquareQuote size={40} className="text-ui-muted mx-auto mb-3" />
              <p className="text-sm font-bold text-brand-dark">No reviews found</p>
              <p className="text-xs text-brand-gray mt-1">
                {search ? 'Try a different search' : 'No reviews in this category yet'}
              </p>
            </div>
          ) : (
            filtered.map((review, i) => (
              <motion.div
                key={review.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: i * 0.02 }}
                className="p-4 sm:p-5 hover:bg-brand-light/30 transition-colors"
              >
                <div className="flex items-start gap-3">
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full bg-brand-red flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-brand-white">
                      {review.name?.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2) || '?'}
                    </span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold text-brand-dark">{review.name}</span>
                      <StarRating rating={review.rating || 5} size={12} />
                      <span className="text-[10px] text-brand-gray">
                        {new Date(review.created_at).toLocaleDateString()}
                      </span>
                      <StatusBadge status={review.status} />
                      {review.is_featured && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-600 font-bold rounded-md">
                          Featured
                        </span>
                      )}
                    </div>

                    <p className="mt-2 text-sm text-brand-dark line-clamp-2">{review.text}</p>

                    {review.products?.title && (
                      <p className="mt-1 text-xs text-brand-gray">
                        Product: <span className="font-semibold text-brand-dark">{review.products.title}</span>
                      </p>
                    )}

                    {review.reviewer_ip && (
                      <p className="text-[10px] text-ui-muted mt-1">IP: {review.reviewer_ip}</p>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-1.5 mt-3 flex-wrap">
                      {review.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleApprove(review.id)}
                            disabled={statusMutation.isPending}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold text-success bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
                          >
                            <CheckCircle size={12} /> Approve
                          </button>
                          <button
                            onClick={() => handleReject(review.id)}
                            disabled={statusMutation.isPending}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold text-error bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                          >
                            <XCircle size={12} /> Reject
                          </button>
                        </>
                      )}
                      {review.status === 'approved' && (
                        <button
                          onClick={() => handleReject(review.id)}
                          disabled={statusMutation.isPending}
                          className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold text-error bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                        >
                          <XCircle size={12} /> Reject
                        </button>
                      )}
                      {review.status === 'rejected' && (
                        <button
                          onClick={() => handleApprove(review.id)}
                          disabled={statusMutation.isPending}
                          className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold text-success bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
                        >
                          <CheckCircle size={12} /> Approve
                        </button>
                      )}
                      <button
                        onClick={() => featuredMutation.mutate({ id: review.id, is_featured: !review.is_featured })}
                        className={clsx(
                          'flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold rounded-lg transition-colors',
                          review.is_featured
                            ? 'text-amber-600 bg-amber-50 hover:bg-amber-100'
                            : 'text-brand-gray bg-brand-light hover:bg-gray-200'
                        )}
                      >
                        {review.is_featured ? <StarOff size={12} /> : <Star size={12} />}
                        {review.is_featured ? 'Unfeature' : 'Feature'}
                      </button>
                      <button
                        onClick={() => setEditReview(review)}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                      >
                        <Edit3 size={12} /> Edit
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(review)}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold text-error bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>

      {/* Edit slide-over */}
      <EditReviewSlideOver
        review={editReview}
        onClose={() => setEditReview(null)}
        mutation={updateMutation}
      />

      {/* Create modal */}
      <CreateReviewModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
      />

      {/* Delete confirm */}
      <AnimatePresence>
        {deleteConfirm && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setDeleteConfirm(null)}
              className="fixed inset-0 z-[60] bg-brand-dark/50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] w-full max-w-sm bg-brand-white rounded-2xl shadow-2xl p-6"
            >
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                  <AlertCircle size={20} className="text-error" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-brand-dark">Delete Review</h3>
                  <p className="text-xs text-brand-gray mt-1">
                    Permanently delete the review by <strong>{deleteConfirm.name}</strong>?
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleDelete(deleteConfirm.id)}
                  disabled={deleteMutation.isPending}
                  className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-bold text-brand-white bg-error hover:bg-red-700 rounded-xl transition-colors"
                >
                  {deleteMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  Delete
                </button>
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2.5 text-sm font-bold text-brand-gray bg-brand-light hover:bg-gray-200 rounded-xl transition-colors"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Status badge ── */
function StatusBadge({ status }) {
  const config = {
    pending: { label: 'Pending', bg: 'bg-amber-50', text: 'text-amber-600' },
    approved: { label: 'Approved', bg: 'bg-green-50', text: 'text-green-600' },
    rejected: { label: 'Rejected', bg: 'bg-red-50', text: 'text-red-600' },
  };
  const c = config[status] || config.pending;
  return (
    <span className={clsx('text-[10px] px-1.5 py-0.5 font-bold rounded-md', c.bg, c.text)}>
      {c.label}
    </span>
  );
}

/* ── Edit slide-over ── */
function EditReviewSlideOver({ review, onClose, mutation }) {
  const [form, setForm] = useState({
    name: '',
    text: '',
    rating: 5,
    product_id: '',
    is_featured: false,
  });
  const { data: products } = useAdminProducts();
  const [initialized, setInitialized] = useState(null);

  // Re-init form when review prop changes
  if (review && initialized !== review.id) {
    setForm({
      name: review.name || '',
      text: review.text || '',
      rating: review.rating || 5,
      product_id: review.product_id || '',
      is_featured: review.is_featured || false,
    });
    setInitialized(review.id);
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!review) return;
    await mutation.mutateAsync({
      id: review.id,
      updates: {
        name: form.name,
        text: form.text?.slice(0, MAX_REVIEW_LENGTH),
        rating: form.rating,
        product_id: form.product_id || null,
        is_featured: form.is_featured,
      },
    });
    onClose();
  };

  return (
    <AnimatePresence>
      {review && (
        <>
          <motion.div
            key="edit-review-overlay"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={onClose}
            className="fixed inset-0 z-40 bg-brand-dark/40 backdrop-blur-sm"
          />
          <motion.div
            key="edit-review-panel"
            variants={panelVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed inset-y-0 right-0 z-50 w-full max-w-lg bg-brand-white shadow-2xl flex flex-col"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-ui-border flex-shrink-0">
              <div>
                <h2 className="text-lg font-extrabold text-brand-dark">Edit Review</h2>
                <p className="text-xs text-brand-gray mt-0.5">By {review.name}</p>
              </div>
              <button onClick={onClose} className="p-2 rounded-lg text-brand-gray hover:bg-brand-light transition-colors">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
              <div>
                <label className="block text-sm font-semibold text-brand-dark mb-1.5">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full px-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-brand-dark mb-1.5">
                  Review Text
                  <span className="text-xs text-brand-gray ml-2">{form.text.length}/{MAX_REVIEW_LENGTH}</span>
                </label>
                <textarea
                  rows={4}
                  value={form.text}
                  onChange={(e) => setForm((f) => ({ ...f, text: e.target.value.slice(0, MAX_REVIEW_LENGTH) }))}
                  className="w-full px-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 resize-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-brand-dark mb-1.5">Rating</label>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, rating: n }))}
                      className="p-1 hover:scale-110 transition-transform"
                    >
                      <Star
                        size={24}
                        className={clsx(
                          n <= form.rating ? 'text-warning fill-warning' : 'text-ui-border'
                        )}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-brand-dark mb-1.5">Product (optional)</label>
                <select
                  value={form.product_id}
                  onChange={(e) => setForm((f) => ({ ...f, product_id: e.target.value }))}
                  className="w-full px-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                >
                  <option value="">No product</option>
                  {(products || []).map((p) => (
                    <option key={p.id} value={p.id}>{p.title}</option>
                  ))}
                </select>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_featured}
                  onChange={(e) => setForm((f) => ({ ...f, is_featured: e.target.checked }))}
                  className="w-4 h-4 rounded border-ui-border text-brand-red focus:ring-brand-red/20"
                />
                <span className="text-sm font-medium text-brand-dark">Featured on homepage</span>
              </label>
            </form>

            <div className="px-6 py-4 border-t border-ui-border flex items-center gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={(e) => {
                  e.currentTarget.closest('.fixed').querySelector('form').requestSubmit();
                }}
                disabled={mutation.isPending}
                className={clsx(
                  'flex items-center gap-2 px-6 py-2.5 text-sm font-bold rounded-xl transition-all',
                  mutation.isPending
                    ? 'bg-brand-red/60 text-brand-white cursor-not-allowed'
                    : 'bg-brand-red hover:bg-red-700 text-brand-white shadow-lg'
                )}
              >
                {mutation.isPending && <Loader2 size={14} className="animate-spin" />}
                Save Changes
              </button>
              <button
                onClick={onClose}
                className="px-6 py-2.5 text-sm font-bold text-brand-gray bg-brand-light hover:bg-gray-200 rounded-xl transition-colors"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

/* ── Create review modal ── */
function CreateReviewModal({ open, onClose }) {
  const createMutation = useCreateReview();
  const { data: products } = useAdminProducts();
  const [form, setForm] = useState({
    name: '',
    text: '',
    rating: 5,
    product_id: '',
    is_featured: false,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    await createMutation.mutateAsync({
      name: form.name,
      text: form.text.slice(0, MAX_REVIEW_LENGTH),
      rating: form.rating,
      product_id: form.product_id || null,
      is_featured: form.is_featured,
    });
    setForm({ name: '', text: '', rating: 5, product_id: '', is_featured: false });
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[60] bg-brand-dark/50 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] w-full max-w-md bg-brand-white rounded-2xl shadow-2xl"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-ui-border">
              <h3 className="text-lg font-extrabold text-brand-dark">Add Review</h3>
              <button onClick={onClose} className="p-1.5 rounded-lg text-brand-gray hover:bg-brand-light transition-colors">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-brand-dark mb-1">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Reviewer name"
                  className="w-full px-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-brand-dark mb-1">
                  Review
                  <span className="text-xs text-brand-gray ml-2">{form.text.length}/{MAX_REVIEW_LENGTH}</span>
                </label>
                <textarea
                  rows={3}
                  value={form.text}
                  onChange={(e) => setForm((f) => ({ ...f, text: e.target.value.slice(0, MAX_REVIEW_LENGTH) }))}
                  placeholder="Write the review..."
                  className="w-full px-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted resize-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-brand-dark mb-1">Rating</label>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, rating: n }))}
                      className="p-0.5 hover:scale-110 transition-transform"
                    >
                      <Star
                        size={22}
                        className={clsx(
                          n <= form.rating ? 'text-warning fill-warning' : 'text-ui-border'
                        )}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-brand-dark mb-1">Product (optional)</label>
                <select
                  value={form.product_id}
                  onChange={(e) => setForm((f) => ({ ...f, product_id: e.target.value }))}
                  className="w-full px-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                >
                  <option value="">No product</option>
                  {(products || []).map((p) => (
                    <option key={p.id} value={p.id}>{p.title}</option>
                  ))}
                </select>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_featured}
                  onChange={(e) => setForm((f) => ({ ...f, is_featured: e.target.checked }))}
                  className="w-4 h-4 rounded border-ui-border text-brand-red focus:ring-brand-red/20"
                />
                <span className="text-sm font-medium text-brand-dark">Featured on homepage</span>
              </label>

              <div className="flex gap-2 pt-2">
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold bg-brand-red hover:bg-red-700 text-brand-white rounded-xl transition-colors"
                >
                  {createMutation.isPending && <Loader2 size={14} className="animate-spin" />}
                  Add Review
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2.5 text-sm font-bold text-brand-gray bg-brand-light hover:bg-gray-200 rounded-xl transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
